import { useRef } from 'react';
import cn from 'classnames';

import Heading from '@components/ui/Heading';
import TagInfodModalContent from '@components/ui/Tags/TagInfoModalContent';
import CategoryCarousels from '@components/algolia/CategoryCarousels';
import PlpBanner from '@components/cms/PlpBanner';
import PlpMerchBoxes from '@components/cms/PlpMerchBoxes';
import { CollectionData } from '@adapters/prismic/types/plp';
import {
  FEATURE_FLAG_PLP_HERO,
  FEATURE_FLAG_HIDE_LEGACY_PLP_BANNER,
  FEATURE_FLAG_PLP_2026_1,
  FEATURE_FLAG_HIDE_LEGACY_PLP_CATEGORY_CAROUSEL,
  FEATURE_FLAG_HIDE_LEGACY_PLP_COLLECTION_CONTENT,
} from '@config/feature-flag';

import styles from '../SearchResults.module.scss';

export type BannerProps = {
  displayBanner: boolean;
  displayCoopComponents?: boolean;
  collectionData?: CollectionData | null;
  locale?: string;
  title?: string;
};

const Banner = ({
  displayBanner,
  displayCoopComponents,
  collectionData,
  locale,
  title,
}: BannerProps) => {
  const headingRef = useRef<HTMLDivElement | null>(null);
  const plpBannerMerchBoxes = useRef<HTMLDivElement | null>(null);

  // FEATURE_FLAG_HIDE_LEGACY_PLP_BANNER hides this legacy image banner
  // once the PLP redesign is live; once it's on, fall through to the
  // (already redesign-styled) bare heading below instead.
  const displayLegacyBanner =
    !FEATURE_FLAG_HIDE_LEGACY_PLP_BANNER &&
    !!collectionData?.plpBannerData?.heroImage?.url;

  return displayBanner && !displayCoopComponents ? (
    <>
      {!(FEATURE_FLAG_PLP_HERO && collectionData?.plpHeroData) &&
      !displayLegacyBanner ? (
        <div ref={headingRef}>
          <Heading
            semanticLevel="h1"
            styleLevel="1"
            className={cn(styles.header, {
              [styles.headerV2]: FEATURE_FLAG_PLP_2026_1,
            })}
          >
            {title}
          </Heading>
        </div>
      ) : null}
      <div ref={plpBannerMerchBoxes} className={cn(styles.bannerContainer)}>
        {!FEATURE_FLAG_HIDE_LEGACY_PLP_BANNER &&
          collectionData?.plpBannerData?.heroImage?.url && (
            <PlpBanner data={collectionData?.plpBannerData} locale={locale} />
          )}
        {!FEATURE_FLAG_HIDE_LEGACY_PLP_CATEGORY_CAROUSEL &&
          collectionData &&
          collectionData?.categoryCarousels?.length !== 0 && (
            <CategoryCarousels
              categoryCarousels={collectionData.categoryCarousels}
            />
          )}
        {!FEATURE_FLAG_HIDE_LEGACY_PLP_COLLECTION_CONTENT &&
          collectionData?.productTagInfoBannerData?.tagName && (
            <div className={styles.productTagInfoBannerContainer}>
              <TagInfodModalContent
                productTagInfoBannerData={
                  collectionData?.productTagInfoBannerData
                }
              />
            </div>
          )}
        {!FEATURE_FLAG_HIDE_LEGACY_PLP_COLLECTION_CONTENT &&
          collectionData !== null &&
          collectionData?.plpMerchBoxesData?.boxes?.length !== 0 && (
            <PlpMerchBoxes
              data={
                collectionData?.plpMerchBoxesData || {
                  numberOfBoxes: '',
                  boxes: [],
                }
              }
              locale={locale}
              hasPlpBanner={displayLegacyBanner}
            />
          )}
      </div>
    </>
  ) : null;
};

export default Banner;
